import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useContext, useMemo } from 'react';
import TagManager from 'react-gtm-module';
import appConfig from '@jobmatchme/library-utils/es/appConfig';
import { v4 as uuid } from 'uuid';
import { getUrlVars } from '@jobmatchme/utils/es/getUrlParam';
import { jsx as _jsx } from "react/jsx-runtime";
var SESSION_KEY = 'jmmSession';
var _getSession = function _getSession() {
  var currentSession;
  try {
    var maybeSession = sessionStorage.getItem(SESSION_KEY);
    if (maybeSession) {
      currentSession = JSON.parse(maybeSession);
    }
  } catch (error) {
    console.error(error);
  }
  if (!currentSession) {
    currentSession = {
      uuid: uuid(),
      urlParams: getUrlVars()
    };
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(currentSession));
    } catch (error) {
      console.error(error);
    }
  }
  return currentSession;
};
var _dataLayerPush = function _dataLayerPush(data) {
  TagManager.dataLayer({
    dataLayer: data,
    dataLayerName: 'PageDataLayer'
  });
};
var _track = function _track(trackingPayload) {
  var eventName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'trackSession';
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var session = _getSession();
  var payload = _extends({
    session: session
  }, trackingPayload, {
    location: window.location.href,
    referrer: document.referrer
  });
  if (navigator.userAgent !== 'ReactSnap') {
    var _trackingPayload = {
      eventName: eventName,
      timestamp: Date.now(),
      payload: payload
    };
    if (!options.noInternal) {
      if (typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon("".concat(appConfig.routes.comApi, "/tracking/create"), JSON.stringify(_trackingPayload));
      } else {
        fetch("".concat(appConfig.routes.comApi, "/tracking/create"), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            Accept: 'application/json'
          },
          body: JSON.stringify(_trackingPayload)
        });
      }
    }
    if (!options.noGtm) {
      var tagManagerArgs = {
        dataLayer: _extends({}, payload, {
          event: payload.tagEvent ? payload.tagEvent : payload.event
        }),
        dataLayerName: 'PageDataLayer'
      };
      _dataLayerPush(tagManagerArgs);
    }
  }
};
var _updateSession = function _updateSession(key, value, didChange, event) {
  var currentSession = _getSession();
  if (didChange && !didChange(currentSession[key], value)) {
    return;
  }
  var newSession = _extends({}, currentSession);
  newSession[key] = value;
  _track({
    event: event || 'updateSession',
    updatedKey: key,
    session: newSession
  });
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
  } catch (error) {
    console.error(error);
  }
};
export var TrackingContext = /*#__PURE__*/React.createContext({
  track2: function track2() {},
  getSession: function getSession() {
    return {};
  },
  updateSession: function updateSession() {},
  dataLayerPush: function dataLayerPush() {}
});
var TrackingProvider = function TrackingProvider(_ref) {
  var children = _ref.children,
    _ref$app = _ref.app,
    app = _ref$app === void 0 ? 'unknownApp' : _ref$app,
    appData = _ref.appData;
  var track2 = useCallback(function (componentData, event, data) {
    var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    _track(_extends({}, appData ? appData : {}, {
      app: app
    }, componentData, {
      event: event,
      type: 'trackEvent',
      data: data
    }), 'trackSession', options);
  }, [app, appData]);
  var tracking = useMemo(function () {
    return {
      track2: track2,
      getSession: _getSession,
      updateSession: _updateSession,
      dataLayerPush: _dataLayerPush
    };
  }, [track2]);
  return /*#__PURE__*/_jsx(TrackingContext.Provider, {
    value: tracking,
    children: children
  });
};
export default TrackingProvider;
var DiscriminatorVerticalMapping = /*#__PURE__*/function (DiscriminatorVerticalMapping) {
  DiscriminatorVerticalMapping["care"] = "care";
  DiscriminatorVerticalMapping["truck"] = "truck";
  DiscriminatorVerticalMapping["logistic-scheduler"] = "scheduler";
  DiscriminatorVerticalMapping["service-gastro"] = "servicegastro";
  return DiscriminatorVerticalMapping;
}(DiscriminatorVerticalMapping || {});
function convertLicense(map, licenses) {
  if (!licenses) {
    return undefined;
  }
  return map.reduce(function (result, _ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
      key = _ref3[0],
      value = _ref3[1];
    if (result) {
      return result;
    }
    if (licenses.includes(key)) return value;else return undefined;
  }, undefined);
}
var convertTruckLicense = function convertTruckLicense(licenses) {
  return convertLicense([['CE', 'license_CE_C_C1E_C1_B'], ['C1E', 'license_C1E_C1_B'], ['C', 'license_C_C1_B'], ['C1', 'license_C1_B'], ['B', 'license_B']], licenses);
};
var convertServiceLicense = function convertServiceLicense(licenses) {
  return convertLicense([['C1', 'license_C1_B_AM'], ['B', 'license_B_AM'], ['AM', 'license_AM']], licenses);
};
var getProfileTrackingObject = function getProfileTrackingObject(pcProfile) {
  var _pcProfile$licenses, _pcProfile$additional, _pcProfile$workTypes, _pcProfile$employment;
  switch (pcProfile.discriminator) {
    case 'logistics-scheduler':
      return {
        customer_vertical: 'scheduler',
        pc_profile_active: pcProfile.active || false
      };
    case 'care':
      return {
        customer_vertical: 'care',
        pc_profile_active: pcProfile.active || false
      };
    case 'truck':
      return {
        customer_vertical: 'truck',
        pc_profile_active: pcProfile.active || false,
        pc_profile_truck_license_c: convertTruckLicense(pcProfile.licenses),
        pc_profile_truck_license_d: (_pcProfile$licenses = pcProfile.licenses) !== null && _pcProfile$licenses !== void 0 && _pcProfile$licenses.includes('D') ? 'license_D' : undefined,
        pc_profile_truck_additional_license: (_pcProfile$additional = pcProfile.additionalLicenses) !== null && _pcProfile$additional !== void 0 && _pcProfile$additional.includes('AdrBasic') ? 'additional_license_adrbasic' : undefined
      };
    case 'service-gastro':
      return {
        customer_vertical: 'servicegastro',
        pc_profile_active: pcProfile.active || false,
        pc_profile_servicegastro_license: convertServiceLicense(pcProfile.driversLicense),
        pc_profile_servicegastro_work_type: (_pcProfile$workTypes = pcProfile.workTypes) === null || _pcProfile$workTypes === void 0 ? void 0 : _pcProfile$workTypes.join(', '),
        pc_profile_servicegastro_employment_type: (_pcProfile$employment = pcProfile.employmentTypes) === null || _pcProfile$employment === void 0 ? void 0 : _pcProfile$employment.join(', ')
      };
    default:
      return {
        customer_vertical: undefined,
        pc_profile_active: false
      };
  }
};
export var useComponentTracking = function useComponentTracking(component, componentData) {
  var tracking = useContext(TrackingContext);
  var track = useCallback(function (event, data) {
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    tracking.track2(componentData ? _extends({}, componentData, {
      component: component
    }) : {
      component: component
    }, event, data, options);
  }, [component, componentData, tracking]);
  var dataLayerPush = useCallback(function (data) {
    tracking.dataLayerPush(_extends({}, data, componentData ? _extends({}, componentData, {
      component: component
    }) : {
      component: component
    }));
  }, [component, componentData, tracking]);
  var customDataLayerPush = useCallback(function (customData) {
    dataLayerPush(customData);
  }, [dataLayerPush]);
  var loginDataLayerPush = useCallback(function (customer_type) {
    customDataLayerPush({
      event: 'login',
      customer_type: customer_type
    });
  }, [customDataLayerPush]);
  var pcDataLayerPush = useCallback(function (event, pcProfile, judgement_source, sign_up_type, step) {
    customDataLayerPush(_extends({
      event: event,
      customer_type: 'pc',
      judgement_source: judgement_source,
      sign_up_type: sign_up_type,
      step: step
    }, getProfileTrackingObject(pcProfile)));
  }, [customDataLayerPush]);
  var bcDataLayerPush = useCallback(function (event, vertical, step) {
    customDataLayerPush({
      event: event,
      customer_type: 'bc',
      customer_vertical: vertical ? DiscriminatorVerticalMapping[vertical] : undefined,
      step: step,
      sign_up_type: event === 'sign_up' ? 'sign_up_regular' : undefined
    });
  }, [customDataLayerPush]);
  var componentTracking = useMemo(function () {
    return {
      track: track,
      getSession: tracking.getSession,
      updateSession: tracking.updateSession,
      dataLayerPush: dataLayerPush,
      loginDataLayerPush: loginDataLayerPush,
      pcDataLayerPush: pcDataLayerPush,
      bcDataLayerPush: bcDataLayerPush
    };
  }, [track, tracking, dataLayerPush, loginDataLayerPush, pcDataLayerPush, bcDataLayerPush]);
  return componentTracking;
};

/** Old functions from frontend-analytics - Integrate as trackInternal or some such at some point and merge with functions above */

export function trackPayload(_ref4) {
  var eventName = _ref4.eventName,
    payload = _ref4.payload;
  var trackingPayload = {
    eventName: eventName,
    timestamp: Date.now(),
    payload: payload
  };
  fetch("".concat(appConfig.routes.comApi, "/tracking/create"), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      Accept: 'application/json'
    },
    body: JSON.stringify(trackingPayload)
  });
}
export function trackEvent(_ref5) {
  var eventName = _ref5.eventName,
    pcUuid = _ref5.pcUuid,
    payload = _ref5.payload;
  trackPayload({
    eventName: eventName,
    payload: _extends({}, payload, {
      pcUuid: pcUuid
    })
  });
}